import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SheerIdForm } from 'Forms/SheerIdForm/SheerIdForm';
import {
  getOrganizations,
  submitActiveMilitaryInfo,
  submitInactiveMilitaryInfo,
  submitFirstResponderPersonalInfo,
} from 'Api/SheerIdService';
import { SheerIdFinePrint } from 'Components/SheerIdFinePrint';
import BangoBackButton from 'Components/BangoBackButton/BangoBackButton';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import {
  SHEER_PAGE_STATUS,
  SHEER_PAGE_TITLES,
  SHEER_PAGE_TYPES,
  SHEER_PAGE_ERROR_MESSAGES,
} from 'Utils/SheerIdHelpers';
import { associateBangoSubscription } from 'Api/SheerIdService';
import { SHEER_FIRST_RESPONDER_TYPE, VETERAN_STATUS } from 'Utils/Constants';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { SHEER_PROGRAM_NAMES } from 'Utils/SheerIdHelpers';
import { SHEER_MILITARY_TYPE } from 'Utils/Constants';
import { getUserSheerInfo } from 'State/Integrations/IntegrationsActions';
import CenterFrameLayout from 'Layouts/CenterFrameLayout';
import './SheerIdFormPage.css';

export const SheerIdFormPage = ({ setPage, setTitle, layoutProps }) => {
  const dispatch = useDispatch();

  const [mountCount, setMountCount] = useState(1);
  const [initialValues, setInitialValues] = useState({});
  const [organizations, setOrganizations] = useState([]);
  // orgSearchQuery is set based on real-time user input (with debounce) from the AsyncInputSearchField component
  //    and is used to query results from the api
  // currentOrgQuery is the search term used to fetch the current set of results. this is used in AsyncInputSearchField
  //    to determine if the results have updated and the dropdown should be refreshed
  const [orgSearchQuery, setOrgSearchQuery] = useState();
  const [currentOrgQuery, setCurrentOrgQuery] = useState('');
  const [formError, setFormError] = useState('');
  const { email } = useSelector((s) => s.user);
  const { subscriptionId, redirectUrl } = useSelector((s) => s.integrations.bango);
  const urlParams = new URLSearchParams(location.search);
  const sheerProgramType = urlParams.get('sheerVerification') || SHEER_MILITARY_TYPE;

  const handleSearchOrganization = (query) => {
    setOrgSearchQuery(query);
  };

  const getFormHeaderText = () => {
    if (sheerProgramType === SHEER_MILITARY_TYPE) {
      return (
        'Next, we must confirm your active military or veteran status. ' +
        'Enter your information as shown on your military documentation.'
      );
    } else if (sheerProgramType === SHEER_FIRST_RESPONDER_TYPE) {
      return (
        'Next, we must confirm you are an active First Responder. ' +
        'Enter your information as shown on your First Responder documentation.'
      );
    }
    return '';
  };

  useEffect(() => {
    const fetchOrgs = async () => {
      let orgOptions;
      const query = sheerProgramType === SHEER_FIRST_RESPONDER_TYPE ? orgSearchQuery ?? '' : null;
      // if this is the firstResponder form, by default the query should be an empty string fetching an empty result
      ({ organizations: orgOptions } = await getOrganizations(SHEER_PROGRAM_NAMES[sheerProgramType], query));
      setCurrentOrgQuery(query);
      setOrganizations(orgOptions);
    };

    fetchOrgs();
    setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.FORM]);
    trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_STARTED, {
      page_partner: subscriptionId ? 'bango' : 'fox',
      page_program: sheerProgramType || SHEER_MILITARY_TYPE,
    });
  }, [setTitle, subscriptionId, sheerProgramType, orgSearchQuery]);

  useEffect(() => {
    const associateSubscription = async () => {
      if (subscriptionId && redirectUrl) {
        try {
          await associateBangoSubscription(subscriptionId, redirectUrl);
        } catch (error) {
          console.error(error);
        }
      }
    };

    associateSubscription();
  }, [subscriptionId, redirectUrl]);

  const getOrgName = (id) => {
    const org = organizations.find((org) => org.id === id);
    return org?.name;
  };

  const onFormSubmit = async ({
    status,
    dobDay,
    dobMonth,
    dobYear,
    dischargeDay,
    dischargeMonth,
    dischargeYear,
    firstName,
    lastName,
    organization,
    search__organization,
  }) => {
    let organizationId, orgName;
    if (typeof organization === 'undefined' && search__organization) {
      organizationId = 0;
      orgName = search__organization;
    } else {
      organizationId = Number(organization);
      orgName = getOrgName(organizationId);
    }

    const payload = {
      data: {
        email,
        firstName,
        lastName,
        birthDate: `${dobYear}-${dobMonth}-${String(dobDay).padStart(2, '0')}`,
        organization: {
          id: organizationId,
          name: orgName,
        },
        ...(sheerProgramType === SHEER_FIRST_RESPONDER_TYPE ? { statuses: [status] } : {}),
      },
      subscriptionRequestId: subscriptionId,
    };

    const goToNextStep = (response) => {
      if (response?.systemErrorMessage) {
        setFormError(response.systemErrorMessage);
        onSubmitReactFormError(response.systemErrorMessage);
      } else if (response?.errorIds?.length) {
        setFormError(SHEER_PAGE_ERROR_MESSAGES[response.errorIds[0]]);
        onSubmitReactFormError(SHEER_PAGE_ERROR_MESSAGES[response.errorIds[0]]);
      } else if (response?.errorMessage) {
        let errorMessage = response.errorMessage;
        if (
          sheerProgramType === SHEER_FIRST_RESPONDER_TYPE &&
          response?.errorMessage === 'missing request variables: [organization.id organization.name]'
        ) {
          errorMessage = SHEER_PAGE_ERROR_MESSAGES.invalidOrganization;
        }
        setFormError(errorMessage);
        return onSubmitReactFormError(errorMessage);
      } else {
        setPage(SHEER_PAGE_STATUS[response?.currentStep], response);
      }
    };

    try {
      let formResult, response;
      if (sheerProgramType === SHEER_MILITARY_TYPE) {
        if (status === VETERAN_STATUS) {
          payload.data.dischargeDate = `${dischargeYear}-${dischargeMonth}-${String(dischargeDay).padStart(2, '0')}`;
          payload['sub-segment'] = 'inactiveMilitary';
          formResult = await submitInactiveMilitaryInfo(payload);
        } else {
          payload['sub-segment'] = 'activeMilitary';
          formResult = await submitActiveMilitaryInfo(payload);
        }
      } else if (sheerProgramType === SHEER_FIRST_RESPONDER_TYPE) {
        formResult = await submitFirstResponderPersonalInfo(payload);
      }
      response = await dispatch(getUserSheerInfo());
      goToNextStep(formResult?.errorIds ? formResult : response);
    } catch (error) {
      goToNextStep(error);
    }
  };

  const remount = (fieldValues, clearDischargeDate, clearOrg) => {
    let finalValues = fieldValues;
    if (clearDischargeDate) {
      const { dischargeDay, dischargeMonth, dischargeYear, ...nonDischargeFields } = finalValues;
      finalValues = nonDischargeFields;
    }
    if (clearOrg) {
      const { organization, ...nonOrgFields } = finalValues;
      finalValues = nonOrgFields;
    }

    setInitialValues(finalValues);
    setMountCount((s) => s + 1);
  };

  return (
    <CenterFrameLayout {...layoutProps}>
      <div className="sheerIdFormPage">
        <BangoBackButton />
        <div className="sheerFormTitle">{getFormHeaderText()}</div>
        <SheerIdForm
          key={mountCount}
          remount={remount}
          initialValues={initialValues}
          onFormSubmit={onFormSubmit}
          organizations={organizations}
          error={formError}
          currentOrgQuery={currentOrgQuery}
          onSearchOrganization={handleSearchOrganization}
        />
        <SheerIdFinePrint />
      </div>
    </CenterFrameLayout>
  );
};
